import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

/**
 * Backs a console's section selection with the URL instead of component state.
 *
 * Operators refresh, use the back button and paste links to colleagues
 * constantly, so a section that only exists in `useState` is a section that
 * cannot be returned to. This keeps the tab strip exactly as it was while
 * giving every section a real address.
 */
export function sectionSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function useSectionRoute<T extends string>(
  base: string,
  sections: readonly T[],
): [T, (next: T) => void] {
  const { section } = useParams<{ section?: string }>()
  const navigate = useNavigate()

  // An unknown or absent slug falls back to the first section rather than
  // rendering nothing — a stale bookmark should still land somewhere useful.
  const active = sections.find((item) => sectionSlug(item) === section) ?? sections[0]

  const setActive = useCallback(
    (next: T) => {
      const slug = sectionSlug(next)
      navigate(slug === sectionSlug(sections[0]) ? base : `${base}/${slug}`)
    },
    [base, navigate, sections],
  )

  return [active, setActive]
}
